import { useApolloClient } from '@apollo/client';
import { faArrowDownToLine, faChevronDown, faChevronUp, faCopy } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AttachmentChip } from './ChatAttachment.tsx';
import {
    CREATED_DOCUMENT_META,
    EMAIL_COMPOSED_STATUSES,
    ERP_WRITE_STATUSES,
    isErpDocumentListOutput,
    isErpDocumentOutput,
    isKnowledgeFilesOutput,
    formatTimestamp,
    getToolEmptyKey,
    getToolIcon,
    getToolResultActions,
    getToolSourceKey,
    humanizeToolName,
    parsePriceResult,
    parseToolOutput,
} from './toolDisplay.tsx';
import {
    buildToolResultRows,
    humanizeKey,
    isArticleListOutput,
    isDocumentListOutput,
    isEmailListOutput,
    isNoDataResult,
    isTableOutput,
} from './toolResultData.ts';
import Button from '../../core/components/Button.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Pagination from '../../core/components/Pagination.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { url } from '../../core/utils/link.ts';
import { GET_SEARCH_RESULTS_PAGE } from '../queries.ts';

const ChevronDownIcon = withIcon(faChevronDown);
const ChevronUpIcon = withIcon(faChevronUp);
const CopyIcon = withIcon(faCopy);
const DownloadIcon = withIcon(faArrowDownToLine);

// label-on-top field with a copy-on-hover button — the result layout used in the chat thread
const ResultField = ({ label, value }: { label: string; value: string }) => (
    <div className="group min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-content-weak">{label}</div>
        <div className="mt-0.5 flex items-start gap-1.5">
            <span className="min-w-0 break-words text-sm font-medium text-content-strong">{value}</span>
            <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(value)}
                className="mt-px flex-none cursor-pointer border-none bg-transparent p-0 text-content-weak opacity-0 transition-opacity hover:text-content-brand-strong group-hover:opacity-100"
            >
                <CopyIcon />
            </button>
        </div>
    </div>
);

const MAX_TOOL_RESULT_ROWS = 8;

// the arguments the LLM called the tool with — same key/value shape as the result rows
const buildToolInputRows = (toolInput): [string, string][] => {
    const parsed = parseToolOutput(toolInput);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
    return Object.entries(parsed).flatMap(([key, value]) => buildToolResultRows(value, humanizeKey(key)));
};

const ToolResultCell = ({ cell }) => {
    const { t } = useTranslation('chat');
    if (cell === null || cell === undefined || cell === '') return <>–</>;
    // a cell holding a URL (e.g. the analytics document link built from the record id) renders as an Open link
    if (typeof cell === 'string' && /^https?:\/\//.test(cell)) {
        return (
            <a href={cell} target="_blank" rel="noreferrer" className="text-content-brand-strong">
                {t('thread.openDocument')}
            </a>
        );
    }
    return <>{String(cell)}</>;
};

const ToolResultTable = ({ output }) => (
    <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
            <thead>
                <tr>
                    {output.columns.map((column, idx) => (
                        <th
                            key={idx}
                            className={classnames(
                                'py-1.5 pr-3 font-semibold capitalize text-content-medium last:pr-0',
                                idx === 0 ? 'text-left' : 'text-right'
                            )}
                        >
                            {humanizeKey(String(column))}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {output.rows.map((row, rowIdx) => (
                    <tr key={rowIdx} className="border-0 border-t border-solid border-line-weak">
                        {row.map((cell, cellIdx) => (
                            <td
                                key={cellIdx}
                                className={classnames(
                                    'py-1.5 pr-3 whitespace-nowrap text-content-strong last:pr-0',
                                    cellIdx === 0 ? 'text-left font-medium' : 'text-right'
                                )}
                            >
                                <ToolResultCell cell={cell} />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// the record search hands the model only its most recent matches plus the total; the remaining pages are
// fetched from the backend on demand, which re-runs the persisted search without another LLM turn.
// The page size follows the rows the model got, so the first page is served from the tool output itself.
const PagedResultTable = ({ output, messageId, toolCallIndex }) => {
    const { t } = useTranslation('chat');
    const client = useApolloClient();
    const pageSize = output.rows.length;
    const [activePage, setActivePage] = useState(0);
    const [pages, setPages] = useState<Record<number, any[]>>({ 0: output.rows });
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    const showPage = async (page: number) => {
        if (pages[page]) {
            setActivePage(page);
            return;
        }
        setLoading(true);
        setFailed(false);
        try {
            const { data } = await client.query({
                query: GET_SEARCH_RESULTS_PAGE,
                variables: { messageId, toolCallIndex, page: page + 1, pageSize },
                fetchPolicy: 'network-only',
            });
            const result = parseToolOutput(data?.agentConversationSearchResultsPage);
            setPages((current) => ({ ...current, [page]: result?.rows ?? [] }));
            setActivePage(page);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className={classnames(loading && 'opacity-50')}>
                <ToolResultTable output={{ ...output, rows: pages[activePage] }} />
            </div>
            {failed && (
                <div className="py-1.5 text-center text-[11px] text-content-danger-strong">
                    {t('thread.pageLoadFailed')}
                </div>
            )}
            <Pagination
                className="justify-center pt-2"
                numItems={output.total}
                itemsPerPage={pageSize}
                activePage={activePage}
                onChange={showPage}
            />
        </div>
    );
};

// One Vorgang as it is worth reading: what it is and whose, then its money, then its positions.
// The generic row flattener would turn the same object into ~20 "line items 1 · article number"
// rows, which is unreadable for a document with more than one position.
const ErpDocumentPanel = ({ document, compact = false }) => {
    const { t } = useTranslation('chat');
    const positions = Array.isArray(document.line_items) ? document.line_items : [];
    const partner = document.partner || {};
    const money = (value) =>
        typeof value === 'number'
            ? `${value.toLocaleString('de-DE', { minimumFractionDigits: 2 })} ${document.currency || ''}`.trim()
            : null;
    const facts = [
        [t('thread.erpDocumentType'), document.transaction_type],
        [t('thread.erpPartner'), [partner.name, partner.address_number].filter(Boolean).join(' · ') || null],
        [t('thread.erpDate'), document.date],
        [t('thread.erpDeliveryDate'), document.delivery_date],
        [t('thread.erpCustomerOrderNumber'), document.customer_order_number],
        [
            t('thread.erpDiscount'),
            typeof document.discount_percent === 'number' ? `${document.discount_percent} %` : null,
        ],
        [t('thread.erpNetTotal'), money(document.net_total)],
    ].filter(([, value]) => Boolean(value));

    return (
        <div className="rounded-md border border-solid border-line-weak p-2">
            <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold text-content-strong">{document.transaction_number}</span>
                {document.is_closed === false && (
                    <span className="text-[10px] uppercase tracking-wide text-content-weak">{t('thread.erpOpen')}</span>
                )}
            </div>
            <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {facts.map(([label, value]) => (
                    <span key={label} className="min-w-0">
                        <span className="text-content-weak">{label}: </span>
                        <span className="break-words text-content-strong">{value}</span>
                    </span>
                ))}
            </div>
            {!compact && positions.length > 0 && (
                <table className="mt-2 w-full border-collapse text-xs">
                    <tbody>
                        {positions.slice(0, MAX_TOOL_RESULT_ROWS).map((position, idx) => (
                            <tr key={idx} className="border-0 border-t border-solid border-line-weak">
                                <td className="py-1 pr-2 text-content-medium">{position.article_number}</td>
                                <td className="py-1 pr-2 text-right text-content-strong">
                                    {position.quantity} {position.unit || ''}
                                </td>
                                <td className="py-1 text-right text-content-weak">{position.delivery_date || ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {!compact && positions.length > MAX_TOOL_RESULT_ROWS && (
                <span className="mt-1 block text-xs text-content-weak">
                    {t('thread.moreRows', { count: positions.length - MAX_TOOL_RESULT_ROWS })}
                </span>
            )}
            {Array.isArray(document.can_be_converted_into) && document.can_be_converted_into.length > 0 && (
                <span className="mt-2 block text-xs text-content-weak">
                    {t('thread.erpCanBecome', { types: document.can_be_converted_into.join(', ') })}
                </span>
            )}
        </div>
    );
};

const InboxEmailRow = ({ email }) => {
    const { t } = useTranslation('chat');
    return (
        <div className="border-0 border-b border-solid border-line-weak py-2 text-xs last:border-b-0">
            <div className="flex items-start justify-between gap-3">
                <span className="min-w-0">
                    <span className="block truncate font-semibold text-content-strong">{email.subject || '–'}</span>
                    <span className="block truncate text-content-medium">
                        {email.from}
                        {email.received_at ? ` · ${formatTimestamp(email.received_at)}` : ''}
                        {email.has_attachments ? ' · 📎' : ''}
                    </span>
                </span>
                {email.web_link && (
                    <Button variant="outline" asChild className="flex-none !px-2 !py-1 text-xs">
                        <a href={email.web_link} target="_blank" rel="noreferrer">
                            {t('thread.openInOutlook')}
                        </a>
                    </Button>
                )}
            </div>
            {email.preview && <p className="m-0 mt-1 line-clamp-2 text-content-weak">{email.preview}</p>}
        </div>
    );
};

const HistoryDocumentRow = ({ document }) => {
    const { t } = useTranslation('chat');
    const path = CREATED_DOCUMENT_META[document.document_type]?.path;
    return (
        <div className="flex items-center justify-between gap-3 border-0 border-b border-solid border-line-weak py-1.5 text-xs last:border-b-0">
            <span className="min-w-0">
                <span className="block truncate font-semibold text-content-strong">
                    {document.subject || document.order_number || document.label}
                </span>
                <span className="block truncate text-content-weak">
                    {[document.date, document.order_number, `${document.line_items?.length ?? 0} items`]
                        .filter(Boolean)
                        .join(' · ')}
                </span>
            </span>
            {path && document.record_id && (
                <Button variant="outline" asChild className="flex-none !px-2 !py-1 text-xs">
                    <Link to={url(path, { recordId: document.record_id })} target="_blank" rel="noreferrer">
                        {t('thread.openDocument')}
                    </Link>
                </Button>
            )}
        </div>
    );
};

// One knowledge file handed over for download: the button needs the attachment the backend created for it
const KnowledgeFileRow = ({ file, attachment }) => {
    const { t } = useTranslation('chat');
    return (
        <div className="flex items-center justify-between gap-3 border-0 border-b border-solid border-line-weak py-1.5 text-xs last:border-b-0">
            <span className="min-w-0">
                <span className="block truncate font-semibold text-content-strong">{file.file_name}</span>
                <span className="block truncate text-content-weak">
                    {t(`thread.knowledgeSources.${file.source_type || 'upload'}`, {
                        defaultValue: t('thread.knowledgeSources.upload'),
                    })}
                </span>
            </span>
            {attachment?.downloadUrl && (
                <Button variant="outline" asChild className="flex-none !px-2 !py-1 text-xs">
                    <a href={attachment.downloadUrl} download={file.file_name}>
                        <DownloadIcon className="mr-1.5" />
                        {t('thread.downloadFile')}
                    </a>
                </Button>
            )}
        </div>
    );
};

// One article candidate: its number and description, then what tells near-identical variants apart
const ArticleCandidateRow = ({ article }) => {
    const extraData =
        article.extra_data && typeof article.extra_data === 'object'
            ? Object.entries(article.extra_data)
                  .map(([key, value]) => `${humanizeKey(key)}: ${value}`)
                  .join(' · ')
            : '';
    const details = [
        article.client_article_number,
        article.article_status,
        article.order_units,
        article.source,
        extraData,
    ].filter(Boolean);
    return (
        <div className="border-0 border-b border-solid border-line-weak py-1.5 text-xs last:border-b-0">
            <span className="block truncate text-content-strong">
                <span className="font-semibold">{article.article_id}</span>
                {article.description && <span className="text-content-medium"> · {article.description}</span>}
            </span>
            {details.length > 0 && <span className="block truncate text-content-weak">{details.join(' · ')}</span>}
        </div>
    );
};

export const ToolResultCard = ({
    toolCall,
    referencedEmailIds = null,
    conversationAttachments = [],
    onAction = null,
    onPreviewAttachment = null,
    disabled = false,
    messageId = null,
    toolCallIndex = null,
}) => {
    const { t } = useTranslation('chat');
    const [showParams, setShowParams] = useState(false);
    const toolName = toolCall.tool_name || '';
    const toolInput = parseToolOutput(toolCall.tool_input);
    const paramRows = buildToolInputRows(toolCall.tool_input);
    const ParamsChevron = showParams ? ChevronUpIcon : ChevronDownIcon;
    const ToolIcon = getToolIcon(toolName);
    const output = parseToolOutput(toolCall.tool_output);
    const failed = Boolean(output && typeof output === 'object' && !Array.isArray(output) && output.error);
    const cancelled = toolCall.status === 'cancelled';
    const skipStructuredOutput = failed || cancelled;
    const sourceKey = getToolSourceKey(toolName);
    const tableOutput = !skipStructuredOutput && isTableOutput(output);
    const documentListOutput = !skipStructuredOutput && !tableOutput && isDocumentListOutput(output);
    const erpDocument = !skipStructuredOutput && !tableOutput && isErpDocumentOutput(output) ? output : null;
    const erpDocumentList =
        !skipStructuredOutput && !tableOutput && !erpDocument && isErpDocumentListOutput(output) ? output : null;
    // a conversion reports the documents it produced rather than one it is
    const erpCreatedDocuments =
        !skipStructuredOutput && output && typeof output === 'object' && Array.isArray(output.created)
            ? output.created
            : null;
    const erpOutput = Boolean(erpDocument || erpDocumentList || erpCreatedDocuments);
    const emailListOutput = !skipStructuredOutput && !tableOutput && !documentListOutput && isEmailListOutput(output);
    const articleListOutput = !skipStructuredOutput && !tableOutput && isArticleListOutput(output);
    const knowledgeFilesOutput = !skipStructuredOutput && isKnowledgeFilesOutput(output);
    const displayedEmails = emailListOutput
        ? referencedEmailIds === null
            ? output.emails
            : output.emails.filter((email) => referencedEmailIds.includes(email.ref))
        : [];
    const webLink = !skipStructuredOutput && output && typeof output === 'object' ? output.web_link : null;
    // an email Worki composed: show its body (which lives in the call's parameters) as a draft panel
    const emailComposed =
        /SendEmail/i.test(toolName) &&
        output &&
        typeof output === 'object' &&
        EMAIL_COMPOSED_STATUSES.has(output.status);
    const draftBody = emailComposed && toolInput && typeof toolInput === 'object' ? toolInput.body : null;
    // resolved from the conversation's full attachment list, not the message's own — an email can reuse a file
    // already shown elsewhere in the chat (an earlier upload, a previous email), so it isn't linked to this message
    const emailAttachments = emailComposed
        ? (output.attachment_ids || [])
              .map((id) => conversationAttachments.find((attachment) => attachment.id === id))
              .filter(Boolean)
        : [];
    // the price tool's raw (price, unit) tuple renders as clean Price / Unit fields
    const priceResult = !skipStructuredOutput && /Price/i.test(toolName) ? parsePriceResult(output) : null;
    const rows: [string, string][] = priceResult
        ? [
              [t('thread.priceLabel'), priceResult.price],
              [t('thread.unitLabel'), priceResult.unit],
          ]
        : (cancelled ||
          tableOutput ||
          documentListOutput ||
          emailListOutput ||
          articleListOutput ||
          knowledgeFilesOutput ||
          emailComposed ||
          erpOutput
              ? []
              : buildToolResultRows(webLink ? { ...output, web_link: undefined } : output)
          ).filter(([, value]) => String(value).trim() !== '');
    const hiddenRows = Math.max(0, rows.length - MAX_TOOL_RESULT_ROWS);

    const actions = getToolResultActions(toolName, output).filter((action) =>
        action.copy ? Boolean(draftBody) : Boolean(onAction)
    );

    const tableEmpty = tableOutput && output.rows.length === 0;
    const pageable =
        tableOutput &&
        Boolean(messageId) &&
        Number.isInteger(toolCallIndex) &&
        toolCallIndex >= 0 &&
        typeof output.total === 'number' &&
        output.total > output.rows.length;
    const documentsEmpty = documentListOutput && output.documents.length === 0;
    const articlesEmpty = articleListOutput && output.results.length === 0;
    const structuredOutput =
        tableOutput || documentListOutput || emailListOutput || articleListOutput || knowledgeFilesOutput || erpOutput;
    // a result that came back without any data — show a per-tool empty state instead of blank fields
    const noData = isNoDataResult(toolCall);

    return (
        <div
            className={classnames(
                'mt-2.5 w-full overflow-hidden whitespace-normal rounded-xl',
                failed ? 'bg-surface-danger-weak' : 'bg-surface-strong/30'
            )}
        >
            <div className="px-4 py-3.5">
                <div className="mb-3 flex items-center gap-2">
                    <ToolIcon className="text-content-brand-strong" />
                    <span className="text-xs font-semibold text-content-strong">{humanizeToolName(toolName)}</span>
                    <span
                        className={classnames(
                            'ml-auto rounded px-1.5 py-0.5 text-[10px] font-semibold',
                            failed && 'bg-surface-danger-weak text-content-danger-strong',
                            cancelled && 'bg-surface-strong text-content-weak',
                            !skipStructuredOutput &&
                                sourceKey === 'sourceErp' &&
                                'bg-surface-brand-medium-default text-content-brand-strong',
                            !skipStructuredOutput &&
                                sourceKey !== 'sourceErp' &&
                                'bg-surface-strong text-content-medium'
                        )}
                    >
                        {failed
                            ? t('thread.toolFailed')
                            : cancelled
                              ? t('thread.toolCancelled')
                              : t(`thread.${sourceKey}`)}
                    </span>
                </div>
                {tableOutput && !tableEmpty && pageable && (
                    <PagedResultTable output={output} messageId={messageId} toolCallIndex={toolCallIndex} />
                )}
                {tableOutput && !tableEmpty && !pageable && <ToolResultTable output={output} />}
                {documentListOutput &&
                    !documentsEmpty &&
                    output.documents.map((document, idx) => <HistoryDocumentRow key={idx} document={document} />)}
                {articleListOutput && !articlesEmpty && (
                    <div>
                        {output.results.slice(0, MAX_TOOL_RESULT_ROWS).map((article, idx) => (
                            <ArticleCandidateRow key={idx} article={article} />
                        ))}
                        <span className="block pt-1.5 text-xs text-content-weak">
                            {t('thread.articleMatches', {
                                shown: Math.min(output.results.length, MAX_TOOL_RESULT_ROWS),
                                total: output.total_count,
                            })}
                        </span>
                    </div>
                )}
                {knowledgeFilesOutput && (
                    <div>
                        {output.files.map((file) => (
                            <KnowledgeFileRow
                                key={file.attachment_id}
                                file={file}
                                attachment={conversationAttachments.find(
                                    (candidate) => candidate.id === file.attachment_id
                                )}
                            />
                        ))}
                        {Array.isArray(output.missing) && output.missing.length > 0 && (
                            <span className="block pt-1.5 text-xs text-content-weak">
                                {t('thread.knowledgeFilesMissing', { names: output.missing.join(', ') })}
                            </span>
                        )}
                    </div>
                )}
                {erpDocument && <ErpDocumentPanel document={erpDocument} />}
                {erpCreatedDocuments && (
                    <div className="flex flex-col gap-2">
                        {ERP_WRITE_STATUSES.has(output.status) && output.source_transaction_number && (
                            <span className="text-xs text-content-weak">
                                {t('thread.erpConvertedFrom', { number: output.source_transaction_number })}
                            </span>
                        )}
                        {erpCreatedDocuments.map((entry, idx) => (
                            <ErpDocumentPanel key={idx} document={entry} />
                        ))}
                    </div>
                )}
                {erpDocumentList && (
                    <div className="flex flex-col gap-2">
                        {erpDocumentList.transactions.slice(0, MAX_TOOL_RESULT_ROWS).map((entry, idx) => (
                            <ErpDocumentPanel key={idx} document={entry} compact />
                        ))}
                        {erpDocumentList.transactions.length > MAX_TOOL_RESULT_ROWS && (
                            <span className="text-xs text-content-weak">
                                {t('thread.moreRows', {
                                    count: erpDocumentList.transactions.length - MAX_TOOL_RESULT_ROWS,
                                })}
                            </span>
                        )}
                    </div>
                )}
                {emailListOutput && displayedEmails.map((email, idx) => <InboxEmailRow key={idx} email={email} />)}
                {emailListOutput && displayedEmails.length === 0 && (
                    <div className="py-1.5 text-xs text-content-weak">{t('thread.noEmails')}</div>
                )}
                {noData && (
                    <div className="py-1.5 text-sm text-content-weak">
                        {t(`thread.noData.${getToolEmptyKey(toolName)}`)}
                    </div>
                )}
                {emailComposed && draftBody && (
                    <div>
                        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-content-weak">
                            {t('thread.draftLabel')}
                        </div>
                        <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg border border-solid border-line-weak bg-surface-weak p-3 font-mono text-xs leading-relaxed text-content-strong">
                            {draftBody}
                        </pre>
                        <p className="m-0 mt-1.5 text-xs text-content-weak">{t('thread.draftAiGenerated')}</p>
                        {emailAttachments.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {emailAttachments.map((attachment) => (
                                    <AttachmentChip
                                        key={attachment.id}
                                        attachment={attachment}
                                        onClick={() => onPreviewAttachment?.(attachment)}
                                        className="border border-solid border-line-strong bg-surface-weak text-content-medium"
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {!structuredOutput && !emailComposed && !noData && rows.length > 0 && (
                    <div className="grid grid-cols-1 gap-x-8 gap-y-3.5 sm:grid-cols-2">
                        {rows.slice(0, MAX_TOOL_RESULT_ROWS).map(([label, value], idx) => (
                            <ResultField key={idx} label={label} value={value} />
                        ))}
                    </div>
                )}
                {hiddenRows > 0 && (
                    <div className="pt-3 text-center text-[11px] text-content-weak">
                        {t('thread.moreRows', { count: hiddenRows })}
                    </div>
                )}
                {webLink && (
                    <div className="flex justify-end pt-2">
                        <Button variant="outline" asChild className="!px-2 !py-1 text-xs">
                            <a href={webLink} target="_blank" rel="noreferrer">
                                {t('thread.openInOutlook')}
                            </a>
                        </Button>
                    </div>
                )}
            </div>
            {actions.length > 0 && (
                <div className="flex flex-wrap gap-2 border-0 border-t border-solid border-line-weak px-4 py-3">
                    {actions.map(({ key, Icon, promptKey, copy }) => (
                        <Button
                            key={key}
                            variant="outline"
                            disabled={disabled}
                            onClick={() =>
                                copy
                                    ? navigator.clipboard?.writeText(draftBody)
                                    : onAction?.(t(`thread.actionPrompts.${promptKey}`))
                            }
                        >
                            <Icon className="mr-1.5" />
                            {t(`thread.actions.${key}`)}
                        </Button>
                    ))}
                </div>
            )}
            {paramRows.length > 0 && (
                <div className="border-0 border-t border-solid border-line-weak">
                    <button
                        type="button"
                        onClick={() => setShowParams((value) => !value)}
                        className="flex w-full cursor-pointer items-center justify-between gap-2 bg-transparent px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-content-weak transition-colors hover:text-content-brand-strong"
                    >
                        {showParams ? t('thread.hideParameters') : t('thread.showParameters')}
                        <ParamsChevron />
                    </button>
                    {showParams && (
                        <div className="bg-surface-strong/40 px-4 pb-1">
                            {paramRows.map(([label, value], idx) => (
                                <div
                                    key={idx}
                                    className="flex justify-between gap-4 border-0 border-b border-solid border-line-weak py-1.5 text-xs last:border-b-0"
                                >
                                    <span className="flex-none capitalize text-content-medium">{label}</span>
                                    <span className="min-w-0 break-words text-right font-medium text-content-strong">
                                        {value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
